import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Loader2, Plus, Trash2, Rocket, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import PoPShell from '../PoPShell';
import { usePoPAuth } from '../../../context/PoPAuthContext';
import { popLogin } from '../../../lib/popApi';
import { capMeta, capCreateProject } from '../../../lib/capstoneApi';
import { Field, inputCls, btnAmber, btnGhost } from '../../../components/capstone/shared';
import GuidePanel from '../../../components/capstone/GuidePanel';
import RefineBar from '../../../components/capstone/RefineBar';

const STEPS = ['Basic info', 'Team setup', 'Charter', 'Confirm & launch'];

const FALLBACK_DOMAINS = [
  'Finance', 'Marketing', 'Operations & Supply Chain', 'Strategy & Consulting',
  'Human Resources', 'Business Analytics & Big Data', 'AI & Data Science',
  'Information Technology & Systems', 'Entrepreneurship & Innovation',
  'International Business', 'Banking & Financial Services', 'Healthcare Management',
  'Sustainability & ESG', 'Digital Transformation', 'Product Management',
  'Retail Management', 'Rural & Agri-Business', 'Hospitality & Tourism',
  'Economics & Public Policy', 'General Management', 'Other',
];

const CHARTER_FIELDS = [
  ['background', 'Background & context', 'e.g. Retail returns in tier-2 cities have risen 30% — the sponsor wants to know why and what to do.'],
  ['objectives', 'Objectives', 'e.g. 1) Identify top 3 drivers of returns; 2) Quantify revenue impact; 3) Recommend a reduction playbook.'],
  ['scope', 'Scope & boundaries', 'e.g. In scope: apparel category, online orders, 2023-25 data. Out of scope: offline stores.'],
  ['success_criteria', 'Success criteria / KPIs', 'e.g. Return-rate driver model with >80% explanatory power; playbook validated by sponsor.'],
  ['beneficiary', 'Target beneficiary / stakeholder', 'e.g. Sponsor CX team; secondarily the university research group.'],
  ['deliverables', 'Expected deliverables', 'e.g. Analysis notebook, final report, executive deck, validated recommendation set.'],
  ['assumptions', 'Assumptions & constraints', 'e.g. Sponsor provides anonymised order data by week 2; no customer PII.'],
];

export default function CapstoneNew() {
  const { user, loading } = usePoPAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [domains, setDomains] = useState(FALLBACK_DOMAINS);
  const [saving, setSaving] = useState(false);

  const [basic, setBasic] = useState({ title: '', problem_statement: '', domain: '', sponsor_org: '', start_date: '', target_end_date: '', team_name: '', cover_image: '' });
  const [members, setMembers] = useState([{ name: '', email: '', role: 'Member' }]);
  const [mentors, setMentors] = useState([{ name: '', email: '' }]);
  const [charter, setCharter] = useState({});
  const [milestones, setMilestones] = useState(null); // null = defaults
  const [authorship, setAuthorship] = useState({});

  useEffect(() => { capMeta().then((m) => { if ((m.domains || []).length) setDomains(m.domains); setMilestones(m.default_milestones.map((n) => ({ name: n }))); }).catch(() => {}); }, []);

  const setB = (k, v) => setBasic((b) => ({ ...b, [k]: v }));

  const onCover = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) { toast.error('Cover image must be under 2MB'); return; }
    const reader = new FileReader();
    reader.onload = () => setB('cover_image', reader.result);
    reader.readAsDataURL(f);
  };

  const canNext = () => {
    if (step === 0) return basic.title.trim() && basic.domain && basic.start_date && basic.target_end_date;
    return true;
  };

  const launch = async () => {
    setSaving(true);
    try {
      const p = await capCreateProject({
        ...basic,
        members: members.filter((m) => m.email.trim() || m.name.trim()),
        mentors: mentors.filter((m) => m.email.trim() || m.name.trim()),
        charter,
        milestones: milestones?.map((m, i) => ({ name: m.name, order: i })),
        authorship,
      });
      toast.success('Capstone project launched');
      navigate(`/pop/capstone/${p.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create project');
    } finally {
      setSaving(false);
    }
  };

  if (!loading && !user) {
    return (
      <PoPShell activeTab="capstone">
        <div className="max-w-md mx-auto text-center py-24">
          <p className="text-sm text-white/60 mb-5">Sign in with your PoP account to create a capstone project.</p>
          <button className={btnAmber} onClick={popLogin}>Sign in</button>
        </div>
      </PoPShell>
    );
  }

  return (
    <PoPShell activeTab="capstone">
      <button onClick={() => navigate('/pop/capstone')} className="inline-flex items-center gap-1.5 text-[12px] text-white/55 hover:text-white mb-6">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Capstone Studio
      </button>
      <div className="max-w-3xl mx-auto">
        <h1 className="font-serif text-3xl mb-2">New capstone project</h1>
        <p className="text-sm text-white/50 mb-8">Four short steps — SAMANVAI Guide can draft the hard parts for you.</p>

        {/* Stepper */}
        <div className="flex items-center gap-2 mb-9" data-testid="cap-wizard-stepper">
          {STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <div className={`flex items-center gap-2 ${i === step ? 'text-amber-300' : i < step ? 'text-emerald-300' : 'text-white/35'}`}>
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold border ${i === step ? 'border-amber-300 bg-amber-300/10' : i < step ? 'border-emerald-300/60' : 'border-white/20'}`}>{i + 1}</span>
                <span className="text-[11.5px] font-semibold hidden sm:inline">{s}</span>
              </div>
              {i < STEPS.length - 1 && <div className="flex-1 h-px bg-white/10" />}
            </React.Fragment>
          ))}
        </div>

        <div className="rounded-2xl border border-white/10 bg-slate-900/50 p-6 sm:p-8">
          {step === 0 && (
            <div className="space-y-5" data-testid="cap-wizard-step-1">
              <RefineBar
                getFields={() => ({ title: basic.title, problem_statement: basic.problem_statement })}
                context={{ domain: basic.domain, sponsor_org: basic.sponsor_org }}
                onApply={(f) => setBasic((b) => ({ ...b, ...f }))}
                label="Refine my title & problem statement"
              />
              <Field label="Project title *">
                <input className={inputCls} value={basic.title} onChange={(e) => setB('title', e.target.value)} placeholder="e.g. Reducing apparel returns for RetailCo" data-testid="cap-title-input" />
              </Field>
              <Field label="One-line problem statement">
                <input className={inputCls} value={basic.problem_statement} onChange={(e) => setB('problem_statement', e.target.value)} placeholder="What are you solving, for whom?" data-testid="cap-problem-input" />
              </Field>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Domain / track *">
                  <select className={inputCls} value={basic.domain} onChange={(e) => setB('domain', e.target.value)} data-testid="cap-domain-select">
                    <option value="">Select…</option>
                    {domains.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </Field>
                <Field label="Sponsor organisation" hint="Optional — industry partner, if any">
                  <input className={inputCls} value={basic.sponsor_org} onChange={(e) => setB('sponsor_org', e.target.value)} placeholder="e.g. RetailCo Pvt Ltd" />
                </Field>
                <Field label="Start date *">
                  <input type="date" className={inputCls} value={basic.start_date} onChange={(e) => setB('start_date', e.target.value)} data-testid="cap-start-input" />
                </Field>
                <Field label="Target end date *">
                  <input type="date" className={inputCls} value={basic.target_end_date} onChange={(e) => setB('target_end_date', e.target.value)} data-testid="cap-end-input" />
                </Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Team name" hint="Optional">
                  <input className={inputCls} value={basic.team_name} onChange={(e) => setB('team_name', e.target.value)} placeholder="e.g. Team Horizon" />
                </Field>
                <Field label="Cover image" hint="Optional · under 2MB">
                  <input type="file" accept="image/*" onChange={onCover} className="text-xs text-white/50 file:mr-3 file:px-3 file:py-1.5 file:rounded file:border-0 file:bg-white/10 file:text-white/80 file:text-xs" />
                </Field>
              </div>
              {basic.cover_image && <img src={basic.cover_image} alt="cover" className="h-24 rounded-lg object-cover" />}
            </div>
          )}

          {step === 1 && (
            <div data-testid="cap-wizard-step-2">
              <div className="text-[10px] tracking-[0.2em] font-bold uppercase text-white/50 mb-3">Team members</div>
              {members.map((m, i) => (
                <div key={i} className="flex flex-wrap gap-2 mb-2 items-center">
                  <input className={`${inputCls} flex-1 min-w-[130px]`} placeholder="Name" value={m.name} onChange={(e) => setMembers((arr) => arr.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} />
                  <input className={`${inputCls} flex-1 min-w-[170px]`} placeholder="email@university.edu" value={m.email} onChange={(e) => setMembers((arr) => arr.map((x, j) => j === i ? { ...x, email: e.target.value } : x))} data-testid={`cap-member-email-${i}`} />
                  <select className={`${inputCls} w-28`} value={m.role} onChange={(e) => setMembers((arr) => arr.map((x, j) => j === i ? { ...x, role: e.target.value } : x))}>
                    <option>Member</option><option>Lead</option>
                  </select>
                  <button onClick={() => setMembers((arr) => arr.filter((_, j) => j !== i))} className="p-2 text-white/35 hover:text-rose-300"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
              <button className={`${btnGhost} mt-1`} onClick={() => setMembers((arr) => [...arr, { name: '', email: '', role: 'Member' }])} data-testid="cap-add-member-btn"><Plus className="w-3.5 h-3.5" /> Add member</button>
              <div className="text-[10.5px] text-white/35 mt-2 italic">You are added as Lead automatically.</div>

              <div className="text-[10px] tracking-[0.2em] font-bold uppercase text-white/50 mb-3 mt-8">Mentor / guide</div>
              {mentors.map((m, i) => (
                <div key={i} className="flex flex-wrap gap-2 mb-2 items-center">
                  <input className={`${inputCls} flex-1 min-w-[130px]`} placeholder="Mentor name" value={m.name} onChange={(e) => setMentors((arr) => arr.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} />
                  <input className={`${inputCls} flex-1 min-w-[170px]`} placeholder="mentor@org.com" value={m.email} onChange={(e) => setMentors((arr) => arr.map((x, j) => j === i ? { ...x, email: e.target.value } : x))} data-testid={`cap-mentor-email-${i}`} />
                  <button onClick={() => setMentors((arr) => arr.filter((_, j) => j !== i))} className="p-2 text-white/35 hover:text-rose-300"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
              <button className={`${btnGhost} mt-1`} onClick={() => setMentors((arr) => [...arr, { name: '', email: '' }])}><Plus className="w-3.5 h-3.5" /> Add mentor</button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6" data-testid="cap-wizard-step-3">
              <RefineBar
                getFields={() => charter}
                context={{ title: basic.title, domain: basic.domain, sponsor_org: basic.sponsor_org }}
                onApply={(f) => {
                  setCharter((c) => ({ ...c, ...f }));
                  setAuthorship((a) => ({ ...a, ...Object.fromEntries(Object.keys(f).map((k) => [`charter.${k}`, 'ai_edited'])) }));
                }}
                label="Refine my charter to CXO-ready language"
              />
              <GuidePanel
                topic="charter_full"
                draftPayload={{ ...basic, charter }}
                label="Let Guide draft the complete charter — all fields at once"
                onUse={(text, origin, s) => {
                  if (s.fields && Object.keys(s.fields).length) {
                    setCharter((c) => ({ ...c, ...s.fields }));
                    setAuthorship((a) => ({ ...a, ...Object.fromEntries(Object.keys(s.fields).map((k) => [`charter.${k}`, origin])) }));
                    toast.success(origin === 'ai' ? 'All charter fields filled by Guide' : 'Charter loaded as a starting point — edit away');
                  } else {
                    setCharter((c) => ({ ...c, background: text }));
                    setAuthorship((a) => ({ ...a, 'charter.background': origin }));
                    toast.success('Guide draft applied');
                  }
                }}
              />
              {CHARTER_FIELDS.map(([key, label, ph]) => (
                <Field key={key} label={label}>
                  <textarea
                    rows={2}
                    className={`${inputCls} resize-y`}
                    placeholder={ph}
                    value={charter[key] || ''}
                    data-testid={`cap-charter-${key}`}
                    onChange={(e) => {
                      setCharter((c) => ({ ...c, [key]: e.target.value }));
                      setAuthorship((a) => (a[`charter.${key}`] ? a : { ...a, [`charter.${key}`]: 'self' }));
                    }}
                  />
                </Field>
              ))}
            </div>
          )}

          {step === 3 && (
            <div data-testid="cap-wizard-step-4">
              <div className="text-[10px] tracking-[0.2em] font-bold uppercase text-white/50 mb-1.5">Milestone plan</div>
              <p className="text-[11.5px] text-white/45 mb-4">These 8 phases are created for you — rename, remove or add. Dates are auto-spread across your timeline.</p>
              <GuidePanel
                topic="milestones"
                draftPayload={{ ...basic, charter }}
                label="Let Guide tune phases to your domain"
                onUse={(text) => {
                  const names = text.split('\n').map((l) => l.split('—')[0].replace(/^[\d.\s-]+/, '').trim()).filter(Boolean);
                  if (names.length >= 3) { setMilestones(names.map((n) => ({ name: n }))); toast.success('Phase template updated'); }
                  else toast.error('Could not parse that suggestion into phases');
                }}
              />
              <div className="mt-4 space-y-2">
                {(milestones || []).map((m, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <GripVertical className="w-3.5 h-3.5 text-white/20" />
                    <span className="text-[11px] text-amber-300/70 font-bold w-6">{String(i + 1).padStart(2, '0')}</span>
                    <input className={`${inputCls} flex-1`} value={m.name} onChange={(e) => setMilestones((arr) => arr.map((x, j) => j === i ? { name: e.target.value } : x))} />
                    <button onClick={() => setMilestones((arr) => arr.filter((_, j) => j !== i))} className="p-2 text-white/35 hover:text-rose-300"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
              <button className={`${btnGhost} mt-3`} onClick={() => setMilestones((arr) => [...(arr || []), { name: 'New phase' }])}><Plus className="w-3.5 h-3.5" /> Add phase</button>

              <div className="mt-8 rounded-xl border border-amber-300/25 bg-amber-300/[0.05] p-4 text-[12.5px] text-white/70">
                <span className="font-bold text-amber-200">{basic.title || 'Untitled project'}</span> · {basic.domain || 'no domain'} · {members.filter((m) => m.email || m.name).length + 1} member(s) · {(milestones || []).length} phases
                {basic.start_date && basic.target_end_date && <> · {basic.start_date} → {basic.target_end_date}</>}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mt-8 pt-6 border-t border-white/8">
            <button className={btnGhost} disabled={step === 0} onClick={() => setStep((s) => s - 1)}>
              <ArrowLeft className="w-3.5 h-3.5" /> Back
            </button>
            {step < 3 ? (
              <button className={btnAmber} disabled={!canNext()} onClick={() => setStep((s) => s + 1)} data-testid="cap-wizard-next-btn">
                Next <ArrowRight className="w-3.5 h-3.5" />
              </button>
            ) : (
              <button className={btnAmber} disabled={saving} onClick={launch} data-testid="cap-wizard-launch-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />} Launch project
              </button>
            )}
          </div>
        </div>
      </div>
    </PoPShell>
  );
}
